import { useState } from "react";
import { Shield, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export const HERO_IMG = "https://customer-assets-lxgj4vgw.emergentagent.net/job_sheraton-platform/artifacts/w95c8apk_sheraton_casestudies-11.jpg";

const DEMO = [
  ["Shift Leader · San Siro", "shiftleader@msegroup.it"],
  ["Operatore · Lake Como", "operatore@msegroup.it"],
  ["Operatore · Malpensa", "operatore.malpensa@msegroup.it"],
  ["Referente · Malpensa", "referente.malpensa@sheraton.it"],
  ["Referente · San Siro", "referente.sansiro@sheraton.it"],
  ["Referente · Lake Como", "referente.como@edition.it"],
];

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try { await login(email, password); }
    catch (err) { setError(formatApiError(err)); toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block">
        <img src={HERO_IMG} alt="Sheraton Hotel" className="absolute inset-0 w-full h-full object-cover" data-testid="login-hero-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent" />
        <div className="absolute bottom-0 p-12 max-w-xl">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-300 mb-4">Sheraton Malpensa · Sheraton San Siro · Lake Como EDITION</div>
          <h2 className="text-4xl font-extrabold text-white leading-tight">Prevenzione, discrezione e controllo in tempo reale.</h2>
          <p className="text-slate-200 mt-4 text-sm leading-relaxed">Turni, ticket con SLA, ronde QR, registri e qualità in un'unica piattaforma per la commessa MSE GROUP.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm fade-up">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-lg bg-amber-600 grid place-items-center"><Shield className="w-5 h-5 text-white" /></div>
            <div>
              <div className="font-heading font-bold text-lg text-slate-900 leading-none">MSE Control</div>
              <div className="eyebrow mt-1 text-[10px]">Facility Management & Security</div>
            </div>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Accedi</h1>
          <p className="text-sm text-slate-500 mt-2 mb-8">Inserisci le tue credenziali aziendali.</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
            </div>
            {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy} className="w-full gap-2 h-11" data-testid="login-submit-button">
              {busy ? "Accesso…" : "Entra"}<ArrowRight className="w-4 h-4" />
            </Button>
          </form>
          <div className="mt-10">
            <div className="eyebrow mb-3">Account demo · password Demo2026!</div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map(([label, mail]) => (
                <button key={mail} type="button" onClick={() => { setEmail(mail); setPassword("Demo2026!"); }} data-testid={`demo-login-${mail.split("@")[0].replace(".", "-")}`}
                  className="text-left px-3 py-2 rounded-lg border border-slate-200 hover:border-amber-600/50 text-xs text-slate-700 transition-colors">
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
